/**
 * Email validation shared by the client (footer subscribe form) and the
 * /api/subscribe serverless function.
 *
 * Deliberately simple (same shape as the contact form and claim routes):
 * one @, non-empty local part, dot-separated domain. The goal is rejecting
 * junk input, not RFC-perfect validation.
 */

export const MAX_EMAIL_LENGTH = 254;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns true when the given string looks like a usable email address.
 * Trims whitespace before checking; empty/oversized strings are invalid.
 */
export const isValidEmail = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false;
  }

  const email = value.trim();
  if (email.length === 0 || email.length > MAX_EMAIL_LENGTH) {
    return false;
  }

  return EMAIL_REGEX.test(email);
};

/**
 * Normalizes an email for storage: trims and lowercases.
 * Returns null when the value is not a string.
 */
export const normalizeEmail = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  return value.trim().toLowerCase();
};
